// @ts-check

import { useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import { LoadingOverlay } from '../common/LoadingOverlay';
import MediaControls from '../mediaControls/MediaControls';

/**
 * Renders a zoom and pan media container component.
 * @typedef {Function} ZoomableMediaContainer
 * @param {object[]} actions of Objects with props for MUI SpeedDial Icons. Expects an array of icons with a tooltipTitle, onClick, and icon prop.
 * @param {children} children that returns the media component to be rendered. Function params are the utils from react-zoom-pan-pinch and the setAspectRatio and setMediaIsLoaded state setters. setAspectRatio is optional but recommended for images and is applied to the TransformComponent.
 * @returns {JSX.Element} The zoomable media container component.
 * TODO: [RENTVINE-8219] Add file download <SpeedDialAction tooltipTitle="Download" icon={<Icon>download</Icon>} />
 */

export default function ZoomableMediaContainer({ actions = [], children }) {
    // check if children is a function, throw error if not.
    if (typeof children !== 'function') {
        throw new Error('children must be a function that returns the media component to be rendered');
    }

    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [isPanning, setIsPanning] = useState(false);
    const [mediaIsLoaded, setMediaIsLoaded] = useState(false);
    const [aspectRatio, setAspectRatio] = useState(null);

    // Default actions. If actions are passed in, they will be added to this array.
    // Hiding zoom in and zoom out on mobile because users can double tap and pinch to zoom and it overflows the container.
    // Not 100% sure if this is the best solution, but it works.
    const actionIcons = ({ zoomIn, zoomOut, centerView }) => [
        {
            tooltipTitle: 'Zoom In',
            onClick: () => zoomIn(),
            icon: 'zoom_in',
            visible: !mobile
        },
        {
            tooltipTitle: 'Zoom Out',
            onClick: () => zoomOut(),
            icon: 'zoom_out',
            visible: !mobile
        },
        {
            tooltipTitle: 'Reset',
            onClick: () => centerView(1, 300, 'easeOut'),
            icon: 'undo'
        },
        ...actions
    ];

    return (
        <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
            <TransformWrapper centerOnInit smooth onPanning={() => setIsPanning(true)} onPanningStop={() => setIsPanning(false)}>
                {utils => (
                    <>
                        {mediaIsLoaded && <MediaControls actionIcons={actionIcons({ ...utils })} />}
                        {!mediaIsLoaded && <LoadingOverlay backgroundColor="common.background" />}

                        <TransformComponent
                            contentStyle={{ height: '100%', cursor: isPanning ? 'grabbing' : 'grab', aspectRatio }}
                            wrapperStyle={{ height: '100%', width: '100%' }}
                        >
                            {children({ setAspectRatio, mediaIsLoaded, setMediaIsLoaded, ...utils })}
                        </TransformComponent>
                    </>
                )}
            </TransformWrapper>
        </Box>
    );
}
